import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Eye, Search } from 'lucide-react';
import { useMyTeaching } from '../../school_panel/lib/teaching';
import { getViewAs, setViewAs, type ViewAs } from '../../school_panel/lib/viewAs';
import type { MeTeaching } from '../../school_panel/api/types';

type ClassRow = NonNullable<MeTeaching['classes']>[number];
interface Group { id: string; name: string; rows: ClassRow[] }

/** Search shows up once the class list is long enough to need it. */
const SEARCH_FROM = 7;

const gradeTitle = (name: string) => (/^grade/i.test(name) ? name : `Grade ${name}`);

/** Top bar: a teacher picks what the panel's figures are for — all their classes, their
 *  homeroom or their subjects (when they hold both), or one of their classes, optionally
 *  one hat inside it. Classes are grouped by grade and folded, so a long list stays short.
 *  Hidden when there is nothing to pick (one class, one role); a stale choice is cleared. */
export default function ViewAsSwitch() {
  const q = useMyTeaching();
  const qc = useQueryClient();
  const [value, setValue] = useState<ViewAs>(getViewAs);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const box = useRef<HTMLDivElement>(null);

  const canSwitch = !!q.data?.can_switch;
  const rows = q.data?.classes ?? [];
  const showClasses = rows.length > 1 || canSwitch;

  // Every key the menu can set, with the label the button shows for it.
  const labels = useMemo(() => {
    const m = new Map<string, string>([['both', 'All my classes']]);
    if (canSwitch) { m.set('homeroom', 'Homeroom'); m.set('subjects', 'My subjects'); }
    if (showClasses) {
      for (const c of rows) {
        m.set(c.key, c.label);
        for (const p of c.parts ?? []) m.set(p.key, `${c.label} · ${p.label}`);
      }
    }
    return m;
  }, [rows, canSwitch, showClasses]);
  const offered = labels.size > 1;

  const groups: Group[] = useMemo(() => {
    const byGrade = new Map<string, Group>();
    for (const c of rows) {
      const id = String(c.grade?.id ?? c.label);
      const g = byGrade.get(id) ?? { id, name: c.grade ? gradeTitle(c.grade.name) : c.label, rows: [] };
      g.rows.push(c);
      byGrade.set(id, g);
    }
    return [...byGrade.values()];
  }, [rows]);

  // The class the current choice belongs to (a hat key is '<class key>:<hat>').
  const currentClass = rows.find((c) => value === c.key || value.startsWith(`${c.key}:`));

  // A remembered choice that no longer exists (assignments changed) goes back to everything.
  useEffect(() => {
    if (q.isSuccess && value !== 'both' && !labels.has(value)) { setViewAs('both'); setValue('both'); }
  }, [q.isSuccess, value, labels]);

  // Opening the menu unfolds the grade in view (or every grade when there are only two).
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setOpenGroups(new Set(groups.length <= 2 ? groups.map((g) => g.id)
      : currentClass ? [String(currentClass.grade?.id ?? currentClass.label)] : []));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!offered) return null;

  const pick = (v: string, close = true) => {
    if (close) setOpen(false);
    if (v === value) return;
    setViewAs(v as ViewAs);
    setValue(v as ViewAs);
    // Every figure on screen depends on the lens: fetch it all again.
    qc.invalidateQueries({ predicate: (query) => query.queryKey[0] !== 'me.teaching' });
  };

  const needle = search.trim().toLowerCase();
  const matches = (c: ClassRow) => !needle || `${c.label} ${c.roles.join(' ')}`.toLowerCase().includes(needle);

  const tick = (on: boolean) => <span className="flex w-4 flex-none justify-center">{on && <Check size={15} strokeWidth={2.2} aria-hidden />}</span>;

  const classRow = (c: ClassRow, inGroup: boolean) => {
    const here = currentClass?.key === c.key;
    const name = inGroup ? (c.section ?? 'All sections') : c.label;
    return (
      <div key={c.key}>
        <button type="button" role="menuitemradio" aria-checked={value === c.key} onClick={() => pick(c.key, !(c.parts?.length))}
          title={`${c.label}: ${c.roles.join(', ')}`}
          className={`flex w-full items-center justify-start gap-2 rounded-[8px] py-1.5 pr-2.5 text-left text-sm hover:bg-surface-2 ${inGroup ? 'pl-6' : 'pl-2.5'} ${here ? 'font-semibold' : 'font-medium'} text-ink`}>
          {tick(value === c.key)}
          <span className="min-w-0 flex-1 truncate text-left">{name}</span>
          <span className="max-w-[55%] flex-none truncate text-xs font-normal text-muted">{c.roles.join(', ')}</span>
        </button>
        {/* Homeroom and a subject in the same class: once picked, narrow to one hat. */}
        {here && (c.parts?.length ?? 0) > 0 && (
          <div role="group" aria-label={`${c.label}: which hat`} className={`mb-1 flex flex-wrap gap-1 ${inGroup ? 'pl-12' : 'pl-8'}`}>
            {[{ key: c.key, label: 'All' }, ...(c.parts ?? [])].map((p) => (
              <button key={p.key} type="button" role="menuitemradio" aria-checked={value === p.key} onClick={() => pick(p.key)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${value === p.key ? 'border-accent bg-accent-bg text-accent-ink' : 'border-line-strong text-muted hover:text-ink'}`}>
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const heading = (t: string) => <p className="px-2.5 pb-1 pt-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{t}</p>;

  return (
    <div ref={box} className="relative">
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={`inline-flex min-h-[44px] max-w-[260px] items-center gap-2 rounded-[12px] border px-3 text-sm font-semibold ${value === 'both' ? 'border-line-strong bg-surface text-ink' : 'border-accent bg-accent-bg text-accent-ink'}`}>
        <Eye size={16} strokeWidth={1.8} aria-hidden className="flex-none" />
        <span className="hidden font-mono text-[11px] uppercase tracking-[0.08em] text-muted md:inline">Viewing</span>
        <span className="truncate">{labels.get(value) ?? 'All my classes'}</span>
        <ChevronDown size={16} strokeWidth={1.8} aria-hidden className="flex-none" />
      </button>
      {open && (
        <div role="menu" aria-label="Viewing as"
          className="absolute right-0 top-[calc(100%+6px)] z-50 flex max-h-[min(60vh,440px)] w-[300px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] border border-line bg-surface shadow-card-hover">
          <div className="flex-none border-b border-hairline p-1.5">
            <button type="button" role="menuitemradio" aria-checked={value === 'both'} onClick={() => pick('both')}
              className="flex w-full items-center justify-start gap-2 rounded-[8px] px-2.5 py-1.5 text-left text-sm font-semibold text-ink hover:bg-surface-2">
              {tick(value === 'both')}<span className="flex-1 text-left">All my classes</span>
            </button>
            {canSwitch && (
              <div role="group" aria-label="As" className="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5">
                <span className="mr-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">As</span>
                {([['homeroom', 'Homeroom', 'Every subject, homeroom class only'], ['subjects', 'My subjects', 'Only the subjects you teach']] as const).map(([k, l, hint]) => (
                  <button key={k} type="button" role="menuitemradio" aria-checked={value === k} title={hint} onClick={() => pick(k)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${value === k ? 'border-accent bg-accent-bg text-accent-ink' : 'border-line-strong text-ink hover:bg-surface-2'}`}>
                    {l}
                  </button>
                ))}
              </div>
            )}
            {showClasses && rows.length >= SEARCH_FROM && (
              <label className="relative mx-1 mt-1.5 flex items-center">
                <Search size={14} aria-hidden className="pointer-events-none absolute left-2.5 text-muted" />
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a class or subject"
                  aria-label="Find a class or subject" autoFocus
                  className="h-9 w-full rounded-[10px] border border-line-strong bg-surface pl-8 pr-2 text-sm text-ink placeholder:text-muted focus:border-slate focus:outline-none" />
              </label>
            )}
          </div>
          {showClasses && (
            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
              {heading('One class')}
              {needle ? (
                rows.filter(matches).map((c) => classRow(c, false))
              ) : groups.map((g) => {
                const unfolded = openGroups.has(g.id);
                const holdsCurrent = g.rows.some((c) => c.key === currentClass?.key);
                return (
                  <div key={g.id}>
                    <button type="button" aria-expanded={unfolded}
                      aria-label={`${g.name}, ${g.rows.length} ${g.rows.length === 1 ? 'class' : 'classes'}${holdsCurrent ? ', viewing one' : ''}`}
                      onClick={() => setOpenGroups((s) => { const n = new Set(s); if (n.has(g.id)) n.delete(g.id); else n.add(g.id); return n; })}
                      className="flex w-full items-center justify-start gap-2 rounded-[8px] px-2.5 py-1.5 text-left text-sm font-semibold text-ink hover:bg-surface-2">
                      <ChevronRight size={15} strokeWidth={2} aria-hidden className={`flex-none text-muted transition-transform ${unfolded ? 'rotate-90' : ''}`} />
                      <span className="flex-1 text-left">{g.name}</span>
                      {holdsCurrent && !unfolded && <span className="h-2 w-2 rounded-full bg-accent" aria-label="Viewing a class here" />}
                      <span className="font-mono text-xs font-normal text-muted">{g.rows.length}</span>
                    </button>
                    {unfolded && g.rows.map((c) => classRow(c, true))}
                  </div>
                );
              })}
              {needle && !rows.some(matches) && <p className="px-2.5 py-2 text-sm text-muted">No class matches.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
